import type { ReactNode } from "react";
import { useHudStore } from "../../store/hudStore";
import type { Note } from "../../types";

const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Resolve a [[wikilink]] target to a loaded note path, Obsidian-style: an exact
// path match first (so [[Atlas/Maps/Design]] is unambiguous), then a basename match
// on any note (so the usual [[Design taste]] finds Atlas/Notes/Design taste.md).
// null means nothing matches - the link renders "dead" (the note doesn't exist yet).
function resolveNote(target: string, notes: Record<string, Note> | null): string | null {
  if (!notes) return null;
  const want = target.replace(/\.md$/i, "").trim().toLowerCase();
  const keys = Object.keys(notes);
  const exact = keys.find((k) => k.replace(/\.md$/i, "").toLowerCase() === want);
  if (exact) return exact;
  const base = keys.find((k) => (k.replace(/\.md$/i, "").split("/").pop() ?? "").toLowerCase() === want);
  return base ?? null;
}

// One [[wikilink]] as an in-app hyperlink into the vault. A resolved link opens the
// note in the viewer; an unresolved one renders muted and inert, matching how
// Obsidian distinguishes an existing note from one you've only referenced.
function WikiLink({ target, alias }: { target: string; alias?: string }) {
  const st = useHudStore();
  const label = alias ?? target;
  const path = resolveNote(target, st.vaultNotes);
  if (!path) return <span className="wikilink--dead" title="Not in the vault yet">{label}</span>;
  return (
    <a
      className="wikilink"
      role="link"
      tabIndex={0}
      title={path}
      onClick={() => st.openNote(path)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); st.openNote(path); } }}
    >
      {label}
    </a>
  );
}

// Inline markdown for note text - [[wikilinks]], **bold**, and `code`. Deliberately
// small (one alternation, no nesting): enough to make links clickable and the rest
// legible, in the same "enough for browsing" spirit as the block parser upstream.
const INLINE = /\[\[([^\]]+)\]\]|\*\*([^*]+)\*\*|`([^`]+)`/g;

export function renderInline(text: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  INLINE.lastIndex = 0;
  for (let m = INLINE.exec(text); m; m = INLINE.exec(text)) {
    const whole = m[0] ?? "";
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = out.length;
    const wiki = m[1], bold = m[2], code = m[3];
    if (wiki !== undefined) {
      const parts = wiki.split("|");
      out.push(<WikiLink key={key} target={(parts[0] ?? "").trim()} alias={parts[1]?.trim()} />);
    } else if (bold !== undefined) {
      out.push(<b key={key} style={{ fontWeight: 700, color: "var(--ink)" }}>{bold}</b>);
    } else if (code !== undefined) {
      out.push(<code key={key} style={{ fontFamily: mono, fontSize: "0.92em", color: "var(--green-ink)", background: "var(--bg2)", border: "1px solid var(--line)", borderRadius: 6, padding: "1px 6px", wordBreak: "break-word" }}>{code}</code>);
    }
    last = m.index + whole.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
